import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  getAllServiceAppointmentsReportData,
  getServiceAppointmentsReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { ServiceTypes } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./ServiceUsage.css";

const tableHdrs = [
  "appointmentDateAndTime",
  "vetName",
  "petName",
  "serviceName",
  "revenue",
  "discount",
  "discountCode",
  "timeSpent",
  "type",
  "category",
  "subcategory",
  "sacCode",
  "variantName",
  "variantNumber",
  "sellingPrice",
];
const filterByList = [
  "Appt Date",
  "Revenue",
  "Discount",
  "Time Spent",
  "Type",
  "Subcategory",
  "SAC Code",
  "Variant Name",
  "Variant ID",
  "Selling Price",
];
const dropFields = ["Type", "Subcategory"];

const initSearch = {
  vetName: "",
  petName: "",
  serviceName: "",
  category: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};

const ServiceLevel2Report = () => {
  const dispatch = useDispatch();
  const hasFetchedAllRef = useRef(false);
  const [selectedVetFilter, setSelectedVetFilter] = useState("");
  const [selectedPetFilter, setSelectedPetFilter] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("all");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { serviceAppointments, allServiceAppointments } = useSelector(
    (state) => state?.report,
  );
  const rowsPerPage = 10;

  useEffect(() => {
    onApiCall(1).then(() => {
      if (hasFetchedAllRef.current) return;
      hasFetchedAllRef.current = true;
      dispatch(getAllServiceAppointmentsReportData());
    });
  }, [
    selectedVetFilter,
    selectedPetFilter,
    selectedCategoryFilter,
    searchValues?.vetName,
    searchValues?.petName,
    searchValues?.serviceName,
    searchValues?.category,
  ]);

  const overviewData = serviceAppointments?.overview || serviceAppointments?.data || {};
  const cards = overviewData?.cards || {};

  const summaryCards = [
    {
      title: "Line items (period)",
      value:
        cards?.lineItemsDisplay ??
        (cards?.lineItems !== undefined
          ? `${Number(cards.lineItems).toLocaleString()}`
          : serviceAppointments?.totalRecords
          ? `${serviceAppointments.totalRecords}`
          : "550"),
    },
  ];

  const handleExportExcel = () => {
    const tableData = serviceAppointments?.data ?? [];
    const exportData = tableData.map((row) => {
      const rawDate = row?.appointmentDateTime || row?.appointmentDateAndTime || row?.createdDate;
      return {
        "Appt date & time": rawDate ? moment(new Date(rawDate)).format("DD MMM YYYY, HH:mm") : "",
        Vet: row?.vetName ?? "",
        Pet: row?.petName ?? "",
        "Service Name": row?.serviceName ?? "",
        Revenue: Number(row?.revenue ?? 0)?.toFixed(2),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Service Usage");
    XLSX.writeFile(workbook, `Service_Usage_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = { type: "detail", page: currentPage, itemSize: rowsPerPage };
    if (reset) return reqObj;
    if (selectedVetFilter) reqObj.vetName = selectedVetFilter;
    if (selectedPetFilter) reqObj.petName = selectedPetFilter;
    if (selectedCategoryFilter !== "all") reqObj.category = selectedCategoryFilter;
    if (searchValues?.vetName?.length > 0) reqObj.vetName = [searchValues?.vetName];
    if (searchValues?.petName?.length > 0) reqObj.petName = [searchValues?.petName];
    if (searchValues?.serviceName?.length > 0) reqObj.serviceName = [searchValues?.serviceName];
    if (searchValues?.category?.length > 0) reqObj.category = searchValues?.category?.map((li) => li?.value);
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    return dispatch(getServiceAppointmentsReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => setEditColModVsble(false);

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((serviceAppointments?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => onApiCall(selectedPage);

  const getTableData = () => {
    return (serviceAppointments?.data ?? [])?.map((li) => {
      const rawDate = li?.appointmentDateTime || li?.appointmentDateAndTime || li?.createdDate;
      return {
        ...li,
        appointmentDateAndTime: rawDate ? moment(new Date(rawDate)).format("DD MMM YYYY, HH:mm") : "-",
        vetName: li?.vetName ?? "",
        petName: li?.petName ?? "",
        serviceName: li?.serviceName ?? "",
        revenue: Number(li?.revenue ?? 0)?.toFixed(2),
      };
    });
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({ ...searchValues, [name?.trim()?.replace(/,$/, "")]: value });
  };

  const onFilterChange = (name, value) => setFilterValues({ ...filterValues, [name]: value });

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
    flushSync(() => setFilterValues({ ...initFilter, selectedList: [], values: {} }));
    onApiCall(1, true);
  };

  const getVetList = () => uniq((serviceAppointments?.data ?? [])?.map((li) => li?.vetName)?.filter(Boolean));
  const getPetList = () => uniq((serviceAppointments?.data ?? [])?.map((li) => li?.petName)?.filter(Boolean));

  const getDropListByType = (typ) => {
    return typ === "Type" ? ServiceTypes : [];
  };

  const onApplyFilter = () => {
    onApiCall();
    setFilterValues({ ...filterValues, visible: false });
  };

  const onCloseFilter = () => setFilterValues({ ...filterValues, visible: false });

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    setFilterValues({ ...filterValues, values: { ...filterValues?.values, [name?.trim()?.replace(/,$/, "")]: value } });
  };

  const onFilterAdd = (value) => setFilterValues({ ...filterValues, added: value });

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) reqList = reqList?.filter((item) => item !== name);
    else reqList.push(name);
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterReset = () => setFilterValues({ ...initFilter, visible: true, selectedList: [], values: {} });

  return (
    <div className="service-usage-container">
      {/* Title */}
      <h2 className="service-usage-title">Service usage</h2>

      {/* Subtitle */}
      <div className="service-usage-subtitle">
        For aggregate service performance, see Service Analytics.
      </div>

      {/* Top Filter Bar */}
      <div className="service-usage-filter-bar">
        <div style={{ width: 180 }}>
          <CustomTextField
            placeholder="Vet name"
            value={selectedVetFilter}
            handleChange={(e) => setSelectedVetFilter(e.target.value)}
            fullWidth
          />
        </div>

        <div style={{ width: 180 }}>
          <CustomTextField
            placeholder="Pet name"
            value={selectedPetFilter}
            handleChange={(e) => setSelectedPetFilter(e.target.value)}
            fullWidth
          />
        </div>

        <Select
          size="small"
          value={selectedCategoryFilter}
          onChange={(e) => setSelectedCategoryFilter(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="all">All categories</MenuItem>
        </Select>
      </div>

      {/* Export Action Buttons */}
      <div className="service-usage-export-group">
        <button type="button" className="service-usage-export-btn" onClick={handleExportExcel}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button type="button" className="service-usage-export-btn" onClick={handleExportPDF}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Card (1 Card) */}
      <div className="service-usage-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="service-usage-card" key={idx}>
            <h4 className="service-usage-card-title">{card.title}</h4>
            <div className="service-usage-card-value-container">
              <h2 className="service-usage-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Drill-down Section with Top Right Edit Columns */}
      <div className="flex-row-between-align-center mv10">
        <div className="service-usage-drilldown-header">Drill-down — service usage</div>
        <div
          className="flex-row-ali-cen cursor"
          onClick={handleEditColumn}
        >
          <EditOutlinedIcon sx={w20h20BlueIcon} />
          <Typography className="blue-color fs16 font-medium ml5">
            Edit columns ({crntTblHeders?.length})
          </Typography>
        </div>
      </div>

      {/* Search Filters Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getVetList()}
              value={searchValues?.vetName ?? []}
              name="vetName"
              handleChange={onSearchChange}
              label="Vet Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getPetList()}
              value={searchValues?.petName ?? []}
              name="petName"
              handleChange={onSearchChange}
              label="Pet Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0 ? `${filterValues?.selectedList?.length} ` : ""}Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Showing {getTableData()?.length ?? 0} of {serviceAppointments?.totalRecords ?? 0} rows for the selected filters
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((serviceAppointments?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>

      {/* Filter Modal */}
      <CustomModal open={filterValues?.visible} onClose={onCloseFilter} header="Filter" rightModal modalWidth={30}>
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="gray38 fs12 txt-regular">Filter by:</Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <CustomCheckbox
                      checked={filterValues?.selectedList?.includes(li)}
                      onChange={() => onSelectFilter(li)}
                    />
                    <Typography className="txt-regular fs14 black mb5 mlMin15">{li}</Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor" onClick={() => onFilterAdd(false)}>
                    <AddIcon sx={{ color: AppColors.appPrimary, fontSize: 15 }} />
                    <Typography className="font-medium fs12 blue-color ml5">Add Filter</Typography>
                  </div>
                  {filterValues?.selectedList?.map((li) => (
                    <div className="mt20" key={li}>
                      {dropFields?.includes(li) ? (
                        <CustomSelect
                          list={getDropListByType(li)}
                          value={filterValues?.values?.[li] ?? []}
                          name={li}
                          handleChange={onFilterValuesChange}
                          label={li}
                          multiSelectSearchCheck
                        />
                      ) : (
                        <CustomTextField
                          placeholder={li}
                          fullWidth
                          name={li}
                          handleChange={onFilterValuesChange}
                          value={filterValues?.values?.[li] ?? ""}
                          label={li}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <CustomButton text="Add" smallBtn onClick={() => onFilterAdd(true)} />
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <CustomButton text="Reset All" smallBtn onClick={onFilterReset} tabSelectdBtn />
                  <div className="ml20">
                    <CustomButton text="Apply Filters" smallBtn onClick={onApplyFilter} />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default ServiceLevel2Report;
